import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Users, Plus, Loader2, DollarSign, Calendar,
  Phone, Mail, Clock, CheckCircle2, AlertCircle, Home
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const paymentStatusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  partial: 'bg-blue-100 text-blue-700'
};

export default function BuyerRentManagementPage() {
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTenantDialog, setShowAddTenantDialog] = useState(false);
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [showRecordPaymentDialog, setShowRecordPaymentDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [newTenant, setNewTenant] = useState({
    name: '',
    phone: '',
    email: '',
    monthly_rent: '',
    security_deposit: '',
    rent_due_day: '1',
    lease_start_date: '',
    lease_end_date: ''
  });

  const [newPayment, setNewPayment] = useState({
    tenant_id: '',
    amount: '',
    month: '',
    due_date: ''
  });

  const [paymentRecord, setPaymentRecord] = useState({
    paid_amount: '',
    paid_date: '',
    payment_method: 'bank_transfer',
    transaction_id: ''
  });

  const buyerData = JSON.parse(localStorage.getItem('buyerData') || '{}');
  const buyerId = buyerData?.id;

  useEffect(() => {
    if (buyerId) {
      loadTenants();
      loadPayments();
    }
  }, [buyerId]);

  const loadTenants = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/tenants?buyer_id=${buyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTenants(data);
      }
    } catch (error) {
      console.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/rent-payments?buyer_id=${buyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPayments(data.payments || []);
      }
    } catch (error) {
      console.error('Failed to load payments');
    }
  };

  const handleAddTenant = async () => {
    if (!newTenant.name || !newTenant.phone || !newTenant.monthly_rent || !newTenant.lease_start_date || !newTenant.lease_end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/tenants?buyer_id=${buyerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newTenant,
          monthly_rent: parseFloat(newTenant.monthly_rent),
          security_deposit: parseFloat(newTenant.security_deposit || 0),
          rent_due_day: parseInt(newTenant.rent_due_day)
        })
      });

      if (res.ok) {
        toast.success('Tenant added successfully');
        setShowAddTenantDialog(false);
        setNewTenant({
          name: '', phone: '', email: '', monthly_rent: '', security_deposit: '',
          rent_due_day: '1', lease_start_date: '', lease_end_date: ''
        });
        loadTenants();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to add tenant');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPayment = async () => {
    if (!newPayment.tenant_id || !newPayment.amount || !newPayment.month || !newPayment.due_date) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/rent-payments?buyer_id=${buyerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newPayment,
          amount: parseFloat(newPayment.amount)
        })
      });

      if (res.ok) {
        toast.success('Payment entry created');
        setShowAddPaymentDialog(false);
        setNewPayment({ tenant_id: '', amount: '', month: '', due_date: '' });
        loadPayments();
      } else {
        throw new Error('Failed to create payment');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentRecord.paid_amount || !paymentRecord.paid_date) {
      toast.error('Please fill in payment details');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/rent-payments/${selectedPayment.id}?buyer_id=${buyerId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'paid',
          paid_amount: parseFloat(paymentRecord.paid_amount),
          paid_date: paymentRecord.paid_date,
          payment_method: paymentRecord.payment_method,
          transaction_id: paymentRecord.transaction_id
        })
      });

      if (res.ok) {
        toast.success('Payment recorded');
        setShowRecordPaymentDialog(false);
        setPaymentRecord({ paid_amount: '', paid_date: '', payment_method: 'bank_transfer', transaction_id: '' });
        loadPayments();
      }
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const activeTenant = tenants.find(t => t.status === 'active');

  // Payment summary
  const totalCollected = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.paid_amount || 0), 0);
  const pendingAmount = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
  const overdueAmount = payments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Rent Management</h1>
          <p className="text-muted-foreground">Manage your property rental and tenants</p>
        </div>
        {!activeTenant && (
          <Button onClick={() => setShowAddTenantDialog(true)} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Tenant
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">₹{totalCollected.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Collected</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">₹{pendingAmount.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">₹{overdueAmount.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeTenant ? 1 : 0}</p>
                <p className="text-xs text-muted-foreground">Active Tenant</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Tenant */}
      {activeTenant ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-600" />
              Current Tenant
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <h3 className="font-semibold text-lg">{activeTenant.name}</h3>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {activeTenant.phone}
                  </span>
                  {activeTenant.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {activeTenant.email}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">₹{activeTenant.monthly_rent?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Monthly Rent</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{activeTenant.rent_due_day}</p>
                  <p className="text-xs text-muted-foreground">Due Day</p>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <div className="text-sm">
                <span className="text-muted-foreground">Lease: </span>
                <span>{new Date(activeTenant.lease_start_date).toLocaleDateString()} - {new Date(activeTenant.lease_end_date).toLocaleDateString()}</span>
              </div>
              <Button variant="outline" onClick={() => setShowAddPaymentDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Rent Entry
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <Home className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg mb-2">No Active Tenant</h3>
            <p className="text-muted-foreground mb-4">
              Add a tenant to start managing rent payments
            </p>
            <Button onClick={() => setShowAddTenantDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Tenant
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>Track all rent payments</CardDescription>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No payment records yet
            </div>
          ) : (
            <div className="space-y-3">
              {payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">{payment.month}</p>
                    <p className="text-sm text-muted-foreground">
                      Due: {new Date(payment.due_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">₹{payment.amount?.toLocaleString()}</p>
                    <Badge className={paymentStatusColors[payment.status]}>
                      {payment.status}
                    </Badge>
                  </div>
                  {payment.status === 'pending' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedPayment(payment);
                        setPaymentRecord({ ...paymentRecord, paid_amount: payment.amount.toString() });
                        setShowRecordPaymentDialog(true);
                      }}
                    >
                      Record Payment
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Tenant Dialog */}
      <Dialog open={showAddTenantDialog} onOpenChange={setShowAddTenantDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Tenant</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tenant Name *</Label>
              <Input
                placeholder="Enter tenant name"
                value={newTenant.name}
                onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  placeholder="+91 9876543210"
                  value={newTenant.phone}
                  onChange={(e) => setNewTenant({ ...newTenant, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="tenant@email.com"
                  value={newTenant.email}
                  onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Rent *</Label>
                <Input
                  type="number"
                  placeholder="25000"
                  value={newTenant.monthly_rent}
                  onChange={(e) => setNewTenant({ ...newTenant, monthly_rent: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Security Deposit</Label>
                <Input
                  type="number"
                  placeholder="50000"
                  value={newTenant.security_deposit}
                  onChange={(e) => setNewTenant({ ...newTenant, security_deposit: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Rent Due Day *</Label>
              <Select
                value={newTenant.rent_due_day}
                onValueChange={(v) => setNewTenant({ ...newTenant, rent_due_day: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 5, 10, 15, 20, 25].map((day) => (
                    <SelectItem key={day} value={day.toString()}>
                      {day}th of every month
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lease Start Date *</Label>
                <Input
                  type="date"
                  value={newTenant.lease_start_date}
                  onChange={(e) => setNewTenant({ ...newTenant, lease_start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Lease End Date *</Label>
                <Input
                  type="date"
                  value={newTenant.lease_end_date}
                  onChange={(e) => setNewTenant({ ...newTenant, lease_end_date: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTenantDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTenant} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Entry Dialog */}
      <Dialog open={showAddPaymentDialog} onOpenChange={setShowAddPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Rent Entry</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {activeTenant && (
              <div className="space-y-2">
                <Label>Tenant</Label>
                <Input value={activeTenant.name} disabled />
                <input type="hidden" value={activeTenant.id} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  placeholder={activeTenant?.monthly_rent?.toString() || '25000'}
                  value={newPayment.amount || activeTenant?.monthly_rent}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Month *</Label>
                <Input
                  type="month"
                  value={newPayment.month}
                  onChange={(e) => setNewPayment({ ...newPayment, month: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={newPayment.due_date}
                onChange={(e) => setNewPayment({ ...newPayment, due_date: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPaymentDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setNewPayment({ ...newPayment, tenant_id: activeTenant?.id });
                handleAddPayment();
              }} 
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={showRecordPaymentDialog} onOpenChange={setShowRecordPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount Paid *</Label>
                <Input
                  type="number"
                  value={paymentRecord.paid_amount}
                  onChange={(e) => setPaymentRecord({ ...paymentRecord, paid_amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={paymentRecord.paid_date}
                  onChange={(e) => setPaymentRecord({ ...paymentRecord, paid_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={paymentRecord.payment_method}
                onValueChange={(v) => setPaymentRecord({ ...paymentRecord, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Transaction ID</Label>
              <Input
                placeholder="Optional"
                value={paymentRecord.transaction_id}
                onChange={(e) => setPaymentRecord({ ...paymentRecord, transaction_id: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordPaymentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
